import {
  CardStatus,
  ICardResponse
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  copyText,
  useModalContext,
  useWSSnackbar,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSImage,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import classNames from "classnames";
import React, { useMemo, useRef, useState } from "react";
import visaDebitLogo from "../../../../assets/images/visa-debit-logo.svg";
import logoMobile from "../../../../assets/images/ws-logo-small.svg";
import { useUnlockCard } from "../../../../query/payments/mutations";
import { IS_PRODUCTION_ENV } from "../../../../shared/constants/environment";
import { useSetWSStore, useWSStore } from "../../../../store";
import styles from "./BankCard.module.scss";
import { BANK_CARD_OTP_MODAL, OTPModalInnerProps } from "./BankCardOTPModal";

export type BankCardProps = {
  card: ICardResponse;
  onDetailsClick?: () => void;
  lockView?: boolean;
} & WSElementProps;

const DATA_STYLE = {
  width: "100%",
  height: "100%",
  fontSize: "17px",
  fontWeight: "600",
  letterSpacing: "0.23em",
  lineHeight: "30px",
  background: "#4e535e",
  color: "white",
  fontFamily: "'PT Mono', monospace"
};

const COPY_BUTTON_STYLE = {
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer"
};

export const vgVaultKey = IS_PRODUCTION_ENV ? "tnt8w6nrmbu" : "tntazhyknp1";

export const BankCard: React.FC<BankCardProps> = ({
  card,
  className,
  onDetailsClick,
  ...elementProps
}) => {
  const { openModal } = useModalContext();
  const { openSnackbar } = useWSSnackbar();

  const [unlockCard, unlockCardMeta] = useUnlockCard();

  const [dataState, setDataState] = useState<
    "hidden" | "requested" | "revealed"
  >("hidden");

  const cardNumberRef = useRef<HTMLElement | null>(null);
  const copyCardNumberRef = useRef<HTMLElement | null>(null);
  const cardCVVRef = useRef<HTMLElement | null>(null);
  const copyCardCVVRef = useRef<HTMLElement | null>(null);

  const expiryDateParts = card.expirationDate.split("-");
  const expiryMonth = expiryDateParts[1];
  const expiryYear = expiryDateParts[0].substring(2);

  const store = useWSStore();
  const setStore = useSetWSStore();

  const show = useMemo(() => {
    return window.VGSShow.create(vgVaultKey, (state: any) => {
      if (Object.keys(state).every(key => state[key].revealed)) {
        setDataState("revealed");
      }
    });
  }, []);

  const requestData = (token: string, cardId: string) => {
    const cardNumber = show.request({
      name: "cardNumber",
      method: "GET",
      path: `/cards/${cardId}/secure-data/pan`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      htmlWrapper: "text",
      serializers: [
        show.SERIALIZERS.replace(
          "(\\d{4})(\\d{4})(\\d{4})(\\d{4})",
          "$1 $2 $3 $4"
        )
      ],
      jsonPathSelector: "data.attributes.pan"
    });
    cardNumber.render(cardNumberRef.current, DATA_STYLE);

    const copyCardNumberButton = show.copyFrom(
      cardNumber,
      {
        text: "Copy card number"
      },
      (status: string) => {
        if (status === "success") {
          openSnackbar({
            message: "Card number is copied",
            icon: {
              name: "clipboard"
            }
          });
        }
      }
    );
    copyCardNumberButton.render(copyCardNumberRef.current, COPY_BUTTON_STYLE);

    const cardCVV = show.request({
      name: "cvv",
      method: "GET",
      path: `/cards/${cardId}/secure-data/cvv2`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      htmlWrapper: "text",
      jsonPathSelector: "data.attributes.cvv2"
    });
    cardCVV.render(cardCVVRef.current, DATA_STYLE);

    const copycardCVVButton = show.copyFrom(
      cardCVV,
      {
        text: "Copy card number"
      },
      (status: string) => {
        if (status === "success") {
          openSnackbar({
            message: "CVV is copied",
            icon: {
              name: "clipboard"
            }
          });
        }
      }
    );
    copycardCVVButton.render(copyCardCVVRef.current, COPY_BUTTON_STYLE);
  };

  const onRevealRequest = () => {
    if (store.bankCardTokens.token && store.bankCardTokens.ids[card.cardId]) {
      setDataState("requested");

      requestData(
        store.bankCardTokens.token,
        store.bankCardTokens.ids[card.cardId]
      );
    } else {
      openModal(BANK_CARD_OTP_MODAL, {
        cardId: card.cardId,
        onSuccess: ({ token, cardId }) => {
          setDataState("requested");

          setStore({
            bankCardTokens: {
              token,
              ids: {
                ...store.bankCardTokens.ids,
                [card.cardId]: cardId
              }
            }
          });

          requestData(token, cardId);
        }
      } as OTPModalInnerProps);
    }
  };

  const isLocked = card.status === CardStatus.Frozen;

  return (
    <WSElement
      className={classNames(className, styles.bankCard, {
        [styles.locked]: isLocked
      })}
      onClick={
        isLocked
          ? async () => {
              await unlockCard(card.cardId);
            }
          : dataState === "hidden"
          ? onRevealRequest
          : undefined
      }
      {...elementProps}
    >
      {isLocked ? (
        <WSFlexBox.Center className={styles.lockedBlocker}>
          {unlockCardMeta.isLoading ? (
            <WSLoader.Spinner />
          ) : (
            <WSIcon block name="lock" size="M" color="white" mr="M" />
          )}
          <WSText weight="medium" color="white">
            Unlock card →
          </WSText>
        </WSFlexBox.Center>
      ) : null}

      {dataState === "requested" && (
        <WSElement className={styles.loader}>
          <WSLoader.Spinner />
        </WSElement>
      )}

      <WSFlexBox>
        <WSElement>
          <WSImage
            src={logoMobile}
            height={22}
            placeholder="Wingspan"
            mb="XL"
          />
        </WSElement>
        {onDetailsClick && (
          <WSButton.Link
            className={styles.detailsButton}
            onClick={onDetailsClick}
            icon="info-circle"
            data-testid="cardDetails"
          >
            Details
          </WSButton.Link>
        )}
      </WSFlexBox>

      {/* <WSLogo size="XS" color="light" className={styles.wsLogo} /> */}

      <WSText className={styles.helpText}>
        {dataState === "revealed"
          ? "Click to copy card details"
          : "Click to reveal card details"}
      </WSText>

      <WSElement
        style={{ display: dataState === "revealed" ? "block" : "none" }}
      >
        <WSElement
          className={classNames(styles.dataLabel, styles.number)}
          ref={cardNumberRef}
        >
          <WSElement className={styles.copyButton} ref={copyCardNumberRef} />
        </WSElement>

        <WSFlexBox>
          <WSElement
            className={classNames(styles.dataLabel, styles.expirationDate)}
            onClick={() => {
              copyText(`${expiryMonth}/${expiryYear}`);
              openSnackbar({
                message: "Expiry date is copied",
                icon: {
                  name: "clipboard"
                }
              });
            }}
          >
            <WSElement className={styles.dataText}>{expiryMonth}</WSElement>
            <WSElement className={classNames(styles.dataText, styles.slash)}>
              /
            </WSElement>
            <WSElement className={styles.dataText}>{expiryYear}</WSElement>
          </WSElement>
          <WSElement
            className={classNames(styles.dataLabel, styles.cvv)}
            ref={cardCVVRef}
          >
            <WSElement className={styles.copyButton} ref={copyCardCVVRef} />
          </WSElement>
        </WSFlexBox>
      </WSElement>

      {dataState !== "revealed" && (
        <>
          <WSElement className={classNames(styles.dataLabel, styles.number)}>
            <PlaceholderDots n={4} className={styles.placeholder} />
            <PlaceholderDots n={4} className={styles.placeholder} />
            <PlaceholderDots n={4} className={styles.placeholder} />

            <WSElement className={styles.dataText}>
              {card.last4Digits}
            </WSElement>
          </WSElement>

          <WSFlexBox>
            <WSElement
              className={classNames(styles.dataLabel, styles.expirationDate)}
            >
              <PlaceholderDots n={2} />
              <WSElement className={classNames(styles.dataText, styles.slash)}>
                /
              </WSElement>
              <PlaceholderDots n={2} />
            </WSElement>
            <WSElement className={classNames(styles.dataLabel, styles.cvv)}>
              <PlaceholderDots n={3} />
            </WSElement>
          </WSFlexBox>
        </>
      )}

      <WSElement className={styles.visaLogo}>
        <WSImage
          src={visaDebitLogo}
          placeholder={<WSText color="white">Visa Debit</WSText>}
        />
      </WSElement>
    </WSElement>
  );
};

const placeholderDot = (
  <svg
    width="6"
    height="6"
    viewBox="0 0 6 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="3" cy="3" r="3" fill="white" />
  </svg>
);

type PlaceholderDotsProps = { n: number } & WSElementProps;

const PlaceholderDots: React.FC<PlaceholderDotsProps> = ({
  n,
  className,
  ...elementProps
}) => (
  <WSElement
    className={classNames(className, styles.placeholderDots)}
    {...elementProps}
  >
    {Array(n)
      .fill(1)
      .map((_, index) => (
        <WSElement className={styles.placeholderDot} key={`${index}`}>
          {placeholderDot}
        </WSElement>
      ))}
  </WSElement>
);

export const JustCreatedPhysicalCard: React.FC<WSElementProps> = props => (
  <WSElement className={styles.newCard} {...props}>
    <WSFlexBox.CenterY className={styles.cardLink} px="XL" py="M">
      <WSText weight="medium" color="gray700">
        Your card is on its way.
      </WSText>
      <WSIcon block ml="M" name="envelope" size="S" color="gray700" />
    </WSFlexBox.CenterY>
  </WSElement>
);

export const NotActivatedPhysicalCard: React.FC<WSElementProps & {
  onClickLink(): void;
}> = ({ onClickLink, ...props }) => (
  <WSElement className={styles.newCard} {...props}>
    <WSFlexBox.CenterY
      className={styles.cardLink}
      px="XL"
      py="M"
      onClick={onClickLink}
    >
      <WSButton.Link data-testid="activateCard">Activate card</WSButton.Link>
      <WSIcon block ml="M" name="open-tab" size="XS" color="blue500" />
    </WSFlexBox.CenterY>
  </WSElement>
);

export const CardSuccessPhysical: React.FC<WSElementProps & {
  isLocked?: boolean;
  isLoading?: boolean;
  last4?: string;
}> = props => {
  const { isLocked, isLoading, last4 } = props;

  return (
    <WSElement className={styles.successCard} {...props}>
      {last4 && (
        <WSText className={styles.last4} color="white">
          {last4}
        </WSText>
      )}
      {isLocked ? (
        <WSFlexBox.Center className={styles.lockedBlocker}>
          {isLoading ? (
            <WSLoader.Spinner />
          ) : (
            <WSIcon block name="lock" size="M" color="white" mr="M" />
          )}
          <WSText weight="medium" color="white">
            Unlock card →
          </WSText>
        </WSFlexBox.Center>
      ) : null}
    </WSElement>
  );
};

export const VirtualCardPlaceholder: React.FC<WSElementProps & {
  onClickLink(): void;
  disabled?: boolean;
}> = ({ onClickLink, disabled, ...props }) => (
  <WSElement
    className={classNames(
      styles.placeholderCard,
      styles.virtualPlaceholderCard
    )}
    {...props}
  >
    {disabled ? (
      <WSText.ParagraphSm
        p="L"
        color="gray600"
        className={styles.placeholderCardBanner}
      >
        We're working on making virtual and physical debit cards available soon.
      </WSText.ParagraphSm>
    ) : null}
    {disabled ? null : (
      <WSButton.Link
        data-testid="orderVirtualFreeCard"
        rightIcon="open-tab"
        disabled={disabled}
        onClick={onClickLink}
        className={styles.cardLink}
      >
        Create free card
      </WSButton.Link>
    )}
  </WSElement>
);

export const PhysicalCardPlaceholder: React.FC<WSElementProps & {
  onClickLink(): void;
  disabled?: boolean;
}> = ({ onClickLink, disabled, ...props }) => (
  <WSElement
    className={classNames(
      styles.placeholderCard,
      styles.physicalPlaceholderCard
    )}
    {...props}
  >
    {disabled ? (
      <WSText.ParagraphSm
        p="L"
        color="gray600"
        className={styles.placeholderCardBanner}
      >
        We're working on making virtual and physical debit cards available soon.
      </WSText.ParagraphSm>
    ) : null}
    {disabled ? null : (
      <WSButton.Link
        data-testid="orderFreeCard"
        rightIcon="open-tab"
        disabled={disabled}
        onClick={onClickLink}
        className={styles.cardLink}
      >
        Order free card
      </WSButton.Link>
    )}
  </WSElement>
);
